import { z } from 'zod';

import {
  actorSnapshotSchema,
  attachmentMetadataSchema,
  entityIdSchema,
  inspectionActorSnapshotSchema,
  nonEmptyTextSchema,
  sha256Schema,
  utcDateTimeSchema,
} from './primitives';
import { resourceScopeSchema } from './tenant-rbac';

export const inspectionJudgementSchema = z.enum(['satisfactory', 'deficient', 'not-applicable']);

export const inspectionTaxonomyMappingSnapshotSchema = z
  .object({
    status: z.enum(['mapped', 'pending-approval', 'unclassified']),
    taxonomyVersion: nonEmptyTextSchema.max(100),
    canonicalRiskCode: z.string().trim().max(100).nullable(),
    riskClassCode: z.string().trim().max(100).nullable(),
  })
  .strict()
  .superRefine((mapping, context) => {
    if (mapping.status === 'mapped' && !mapping.canonicalRiskCode) {
      context.addIssue({
        code: 'custom',
        path: ['canonicalRiskCode'],
        message: 'Mapped inspection taxonomy requires an approved canonical risk code',
      });
    }
    if (mapping.status !== 'mapped' && (mapping.canonicalRiskCode || mapping.riskClassCode)) {
      context.addIssue({
        code: 'custom',
        path: ['canonicalRiskCode'],
        message: 'Unapproved taxonomy values cannot carry canonical aggregation codes',
      });
    }
  });

export const inspectionDeficiencySchema = z
  .object({
    location: nonEmptyTextSchema.max(300),
    description: nonEmptyTextSchema.max(2_000),
    responsibleOrganizationId: entityIdSchema,
    evidence: z.array(attachmentMetadataSchema).min(1).max(2),
  })
  .strict();

export const inspectionKpiClassSchema = z.enum([
  'hazard',
  'facility-improvement',
  'simple-cleaning',
]);

export const inspectionKpiPolicyIdSchema = z.literal('eumseong-employee-safety-activity-v1');

export const inspectionItemFindingSchema = z
  .object({
    findingId: entityIdSchema,
    deficiency: inspectionDeficiencySchema,
  })
  .strict();

export const inspectionAttendanceSignatureSchema = z
  .object({
    strokes: z
      .array(z.string().regex(/^\d{1,4},\d{1,4}(?: \d{1,4},\d{1,4})+$/u))
      .min(1)
      .max(64),
    sha256: sha256Schema,
    capturedAt: utcDateTimeSchema,
  })
  .strict()
  .superRefine((signature, context) => {
    let pointCount = 0;
    let outOfBounds = false;
    for (const stroke of signature.strokes) {
      const points = stroke.split(' ');
      pointCount += points.length;
      for (const point of points) {
        const [x = -1, y = -1] = point.split(',').map(Number);
        if (x < 0 || y < 0 || x > 1_000 || y > 1_000) outOfBounds = true;
      }
    }
    if (pointCount < 8 || pointCount > 1_500) {
      context.addIssue({
        code: 'custom',
        path: ['strokes'],
        message: 'Attendance signature point count is outside the canvas evidence limit',
      });
    }
    if (outOfBounds) {
      context.addIssue({
        code: 'custom',
        path: ['strokes'],
        message: 'Attendance signature coordinates must remain in the normalized canvas',
      });
    }
  });

export const inspectionParticipantSchema = z
  .object({
    participantId: entityIdSchema,
    participantKind: z.enum(['registered-user', 'field-worker']),
    identityId: entityIdSchema.nullable(),
    displayNameSnapshot: nonEmptyTextSchema.max(100),
    organizationId: entityIdSchema.nullable(),
    organizationNameSnapshot: nonEmptyTextSchema.max(200),
    jobTitleSnapshot: z.string().trim().max(100).nullable(),
    signature: inspectionAttendanceSignatureSchema,
  })
  .strict()
  .superRefine((participant, context) => {
    if (participant.participantKind === 'registered-user' && participant.identityId === null) {
      context.addIssue({
        code: 'custom',
        path: ['identityId'],
        message: 'A registered inspection participant requires an identity reference',
      });
    }
    if (participant.participantKind === 'field-worker' && participant.identityId !== null) {
      context.addIssue({
        code: 'custom',
        path: ['identityId'],
        message: 'A field worker is an inspection-only snapshot without a login identity',
      });
    }
  });

export const quickInspectionCategorySnapshotSchema = z
  .object({
    categoryId: entityIdSchema,
    label: nonEmptyTextSchema.max(200),
  })
  .strict();

export const quickInspectionResolutionModeSchema = z.enum(['contractor-request', 'immediate']);

export const quickInspectionFindingInputSchema = z
  .object({
    categoryId: entityIdSchema,
    workLocationId: entityIdSchema,
    contractorId: entityIdSchema,
    engagementId: entityIdSchema,
    resolutionMode: quickInspectionResolutionModeSchema,
    immediateAction: z
      .object({
        description: nonEmptyTextSchema.max(4_000),
        afterEvidence: z.array(attachmentMetadataSchema).min(1).max(2),
      })
      .strict()
      .nullable(),
  })
  .strict()
  .superRefine((input, context) => {
    if ((input.resolutionMode === 'immediate') !== (input.immediateAction !== null)) {
      context.addIssue({
        code: 'custom',
        path: ['immediateAction'],
        message: 'Only an immediate quick finding carries completed action evidence',
      });
    }
  });

export const inspectionFindingWorkInputSchema = z
  .object({
    workTradeId: entityIdSchema,
    workLocationId: entityIdSchema,
    contractorId: entityIdSchema,
    engagementId: entityIdSchema,
  })
  .strict();

export const inspectionFindingWorkContextSchema = z
  .object({
    tradeId: entityIdSchema,
    tradeLabelSnapshot: nonEmptyTextSchema.max(300),
    tradeVersion: z.number().int().positive(),
    workLocations: z
      .array(
        z
          .object({
            workLocationId: entityIdSchema,
            labelSnapshot: nonEmptyTextSchema.max(300),
            version: z.number().int().positive(),
          })
          .strict(),
      )
      .length(1),
    contractorSnapshot: z
      .object({
        contractorId: entityIdSchema,
        contractorName: nonEmptyTextSchema.max(200),
        contractorVersion: z.number().int().positive(),
        engagementId: entityIdSchema,
        engagementVersion: z.number().int().positive(),
      })
      .strict(),
    pinnedAt: utcDateTimeSchema,
    source: z.literal('eumseong-inspection-master-registry'),
    immutable: z.literal(true),
  })
  .strict();

export const quickInspectionFindingContextSchema = z
  .object({
    categorySnapshot: quickInspectionCategorySnapshotSchema.extend({
      version: z.number().int().positive(),
    }),
    workLocationSnapshot: z
      .object({
        workLocationId: entityIdSchema,
        label: nonEmptyTextSchema.max(300),
        version: z.number().int().positive(),
      })
      .strict(),
    workTradeSnapshot: z
      .object({
        tradeId: entityIdSchema,
        label: nonEmptyTextSchema.max(300),
        version: z.number().int().positive(),
      })
      .strict(),
    contractorEngagementSnapshot: z
      .object({
        contractorId: entityIdSchema,
        contractorName: nonEmptyTextSchema.max(200),
        contractorVersion: z.number().int().positive(),
        engagementId: entityIdSchema,
        engagementVersion: z.number().int().positive(),
      })
      .strict(),
    resolutionMode: quickInspectionResolutionModeSchema,
    pinnedAt: utcDateTimeSchema,
    source: z.literal('eumseong-inspection-master-registry'),
    immutable: z.literal(true),
  })
  .strict();

export const inspectionChecklistPinSchema = z
  .object({
    templateId: entityIdSchema,
    templateVersion: z.number().int().positive(),
    templateHash: sha256Schema,
    name: nonEmptyTextSchema.max(300),
    kind: z.enum(['routine', 'special']),
  })
  .strict();

export const inspectionItemResultSchema = z
  .object({
    itemId: entityIdSchema,
    sequence: z.number().int().positive(),
    promptSnapshot: nonEmptyTextSchema.max(2_000),
    judgement: inspectionJudgementSchema,
    note: z.string().trim().max(2_000).nullable(),
    taxonomyMappingSnapshot: inspectionTaxonomyMappingSnapshotSchema.nullable().optional(),
    deficiency: inspectionDeficiencySchema.nullable(),
    findingId: entityIdSchema.nullable(),
    findings: z.array(inspectionItemFindingSchema).min(1).optional(),
  })
  .strict()
  .superRefine((item, context) => {
    const usesMultipleFindingModel = item.findings !== undefined;
    if (item.judgement === 'deficient' && item.deficiency === null && !usesMultipleFindingModel) {
      context.addIssue({
        code: 'custom',
        path: ['deficiency'],
        message: 'A deficient inspection item requires deficiency details and evidence',
      });
    }
    if (usesMultipleFindingModel && (item.deficiency !== null || item.findingId !== null)) {
      context.addIssue({
        code: 'custom',
        path: ['findings'],
        message: 'The multiple-finding model cannot be mixed with the legacy single finding link',
      });
    }
    if (
      item.judgement !== 'deficient' &&
      (item.deficiency !== null || item.findingId !== null || usesMultipleFindingModel)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['deficiency'],
        message: 'Only deficient items can carry deficiency details or a finding link',
      });
    }
  });

export const inspectionStatusSchema = z.enum([
  'draft',
  'submitted',
  'under-review',
  'approved',
  'rejected',
  'closed',
]);

export const specialInspectionContextSchema = z
  .object({
    campaignId: entityIdSchema,
    campaignTitle: nonEmptyTextSchema.max(300),
    purpose: nonEmptyTextSchema.max(2_000),
    requestingOrganization: nonEmptyTextSchema.max(300),
    requestReference: nonEmptyTextSchema.max(500),
    periodStart: z.iso.date(),
    periodEnd: z.iso.date(),
    targetSiteId: entityIdSchema,
    targetArea: nonEmptyTextSchema.max(300),
    roundId: entityIdSchema,
    roundNumber: z.number().int().positive(),
    roundTitle: nonEmptyTextSchema.max(300),
    templateSnapshot: z
      .object({
        templateId: entityIdSchema,
        templateVersion: z.number().int().positive(),
        templateHash: sha256Schema,
        title: nonEmptyTextSchema.max(300),
        capturedAt: utcDateTimeSchema,
      })
      .strict(),
  })
  .strict()
  .superRefine((context, refinement) => {
    if (context.periodStart > context.periodEnd) {
      refinement.addIssue({
        code: 'custom',
        path: ['periodEnd'],
        message: 'Special inspection period end must not precede its start',
      });
    }
  });

export const inspectionSchema = z
  .object({
    inspectionId: entityIdSchema,
    documentVersionId: entityIdSchema,
    scope: resourceScopeSchema,
    inspectionType: z.enum(['routine', 'special', 'quick', 'contractor-daily']),
    inspectionCategory: z.enum(['structured', 'special', 'quick']).optional(),
    structuredKind: z.enum(['joint', 'weekly', 'patrol', 'night']).nullable().optional(),
    areaId: entityIdSchema.optional(),
    templateId: entityIdSchema.nullable(),
    templateVersionId: entityIdSchema.nullable(),
    checklist: inspectionChecklistPinSchema.nullable().optional(),
    quickCategory: quickInspectionCategorySnapshotSchema.nullable().optional(),
    inspectedAt: utcDateTimeSchema,
    inspector: inspectionActorSnapshotSchema,
    participants: z.array(inspectionParticipantSchema).optional(),
    kpiPolicyId: inspectionKpiPolicyIdSchema.nullable().optional(),
    kpiCreditedIdentityIds: z.array(entityIdSchema).optional(),
    status: inspectionStatusSchema,
    items: z.array(inspectionItemResultSchema).min(1),
    specialContext: specialInspectionContextSchema.nullable().optional(),
    submittedAt: utcDateTimeSchema.nullable(),
  })
  .strict()
  .superRefine((inspection, context) => {
    const category =
      inspection.inspectionCategory ?? (inspection.inspectionType === 'quick' ? 'quick' : null);
    if (category === 'quick') {
      if (inspection.templateId !== null || inspection.templateVersionId !== null) {
        context.addIssue({
          code: 'custom',
          path: ['templateId'],
          message: 'A quick inspection does not pin a checklist template',
        });
      }
      if (inspection.quickCategory !== null && inspection.quickCategory !== undefined) {
        context.addIssue({
          code: 'custom',
          path: ['quickCategory'],
          message: 'Quick inspection categories are pinned independently on each finding',
        });
      }
      if (inspection.checklist) {
        context.addIssue({
          code: 'custom',
          path: ['checklist'],
          message: 'A quick inspection cannot carry a checklist template pin',
        });
      }
    } else if (category !== null && (!inspection.templateId || !inspection.templateVersionId)) {
      context.addIssue({
        code: 'custom',
        path: ['templateId'],
        message: 'Structured and special inspections require a pinned checklist template',
      });
    } else if (category !== null && !inspection.checklist) {
      context.addIssue({
        code: 'custom',
        path: ['checklist'],
        message: 'Structured and special inspections require an immutable checklist pin',
      });
    }
    if (category === 'structured' && !inspection.structuredKind) {
      context.addIssue({
        code: 'custom',
        path: ['structuredKind'],
        message: 'A structured inspection requires its operating kind',
      });
    }
    if (category !== 'structured' && inspection.structuredKind) {
      context.addIssue({
        code: 'custom',
        path: ['structuredKind'],
        message: 'Only a structured inspection carries a structured kind',
      });
    }
    const participants = inspection.participants ?? [];
    if (
      new Set(participants.map((participant) => participant.participantId)).size !==
      participants.length
    ) {
      context.addIssue({
        code: 'custom',
        path: ['participants'],
        message: 'Inspection participant identifiers must be unique',
      });
    }
    if (inspection.status !== 'draft' && inspection.submittedAt === null) {
      context.addIssue({
        code: 'custom',
        path: ['submittedAt'],
        message: 'A non-draft inspection requires submittedAt',
      });
    }

    const sequences = inspection.items.map((item) => item.sequence);
    if (new Set(sequences).size !== sequences.length) {
      context.addIssue({
        code: 'custom',
        path: ['items'],
        message: 'Inspection item sequences must be unique',
      });
    }
    if (inspection.status !== 'draft') {
      inspection.items.forEach((item, index) => {
        if (
          item.judgement === 'deficient' &&
          item.findingId === null &&
          item.findings === undefined
        ) {
          context.addIssue({
            code: 'custom',
            path: ['items', index, 'findingId'],
            message: 'Submitted deficient items must link to a generated finding',
          });
        }
      });
    }
  });

export const findingRiskSchema = z.enum(['low', 'medium', 'high', 'critical']);
export const findingStatusSchema = z.enum([
  'received',
  'assigned',
  'action-in-progress',
  'verification-requested',
  'approval-pending',
  'completed',
  'cancelled',
]);

export const findingSourceSnapshotSchema = z
  .object({
    sourceType: z.enum(['inspection', 'field-report', 'facility', 'psm', 'meeting', 'feedback']),
    sourceId: entityIdSchema,
    sourceVersionId: entityIdSchema.nullable(),
    sourceItemId: entityIdSchema.nullable(),
    summary: nonEmptyTextSchema.max(2_000),
    occurredAt: utcDateTimeSchema,
    snapshotHash: sha256Schema,
  })
  .strict();

export const findingAssignmentSchema = z
  .object({
    responsibleOrganizationId: entityIdSchema,
    responsibleUserId: entityIdSchema.nullable(),
    dueAt: utcDateTimeSchema.nullable(),
    assignedAt: utcDateTimeSchema,
    assignedBy: inspectionActorSnapshotSchema,
  })
  .strict();

export const findingActionSubmissionSchema = z
  .object({
    submissionId: entityIdSchema,
    description: nonEmptyTextSchema.max(4_000),
    recurrencePrevention: z.string().trim().max(4_000).nullable(),
    beforeEvidence: z.array(attachmentMetadataSchema),
    afterEvidence: z.array(attachmentMetadataSchema).min(1),
    submittedBy: actorSnapshotSchema,
    submittedAt: utcDateTimeSchema,
  })
  .strict();

export const findingVerificationSchema = z
  .object({
    approved: z.boolean(),
    opinion: nonEmptyTextSchema.max(2_000),
    verifiedBy: actorSnapshotSchema,
    verifiedAt: utcDateTimeSchema,
  })
  .strict();

/**
 * Read-only provenance for a legacy remediation that the source recorded as complete without a
 * canonical KOP verification. This never grants approval authority and is rejected by live-create.
 */
export const historicalFindingCompletionSchema = z
  .object({
    state: z.literal('legacy-completion-observed'),
    approvalImported: z.literal(false),
    basis: z.enum(['explicit-repair-status', 'repair-photo-compatibility']),
    completedAt: utcDateTimeSchema,
    sourceSnapshotHash: sha256Schema,
    afterEvidence: z.array(attachmentMetadataSchema).min(1).max(2),
    immutable: z.literal(true),
  })
  .strict();

export const findingCancellationSchema = z
  .object({
    reason: nonEmptyTextSchema.max(2_000),
    cancelledBy: actorSnapshotSchema,
    cancelledAt: utcDateTimeSchema,
  })
  .strict();

export const findingSchema = z
  .object({
    findingId: entityIdSchema,
    scope: resourceScopeSchema,
    source: findingSourceSnapshotSchema,
    risk: findingRiskSchema,
    quickContext: quickInspectionFindingContextSchema.optional(),
    workContext: inspectionFindingWorkContextSchema.optional(),
    kpiPolicyId: inspectionKpiPolicyIdSchema.nullable().optional(),
    kpiClass: inspectionKpiClassSchema.optional(),
    kpiAttributedToUid: entityIdSchema.nullable().optional(),
    kpiAttributedAt: utcDateTimeSchema.optional(),
    status: findingStatusSchema,
    assignment: findingAssignmentSchema.nullable(),
    actionSubmissions: z.array(findingActionSubmissionSchema),
    verification: findingVerificationSchema.nullable(),
    historicalCompletion: historicalFindingCompletionSchema.optional(),
    cancellation: findingCancellationSchema.nullable(),
    recurrenceOfFindingId: entityIdSchema.nullable(),
    createdAt: utcDateTimeSchema,
    updatedAt: utcDateTimeSchema,
  })
  .strict()
  .superRefine((finding, context) => {
    if (
      finding.assignment?.dueAt === null &&
      finding.quickContext?.resolutionMode !== 'immediate'
    ) {
      context.addIssue({
        code: 'custom',
        path: ['assignment', 'dueAt'],
        message: 'Only an immediate quick finding can omit a due date',
      });
    }
    if (
      finding.quickContext?.resolutionMode === 'contractor-request' &&
      !finding.assignment?.dueAt
    ) {
      context.addIssue({
        code: 'custom',
        path: ['assignment', 'dueAt'],
        message: 'A contractor-request quick finding requires its trusted business due date',
      });
    }
    if (
      finding.quickContext?.resolutionMode === 'immediate' &&
      finding.assignment?.dueAt !== null
    ) {
      context.addIssue({
        code: 'custom',
        path: ['assignment', 'dueAt'],
        message: 'An immediate quick finding does not carry a due date',
      });
    }
    if (
      finding.status !== 'received' &&
      finding.status !== 'cancelled' &&
      finding.assignment === null
    ) {
      context.addIssue({
        code: 'custom',
        path: ['assignment'],
        message: 'Assigned and later findings require an assignment snapshot',
      });
    }
    if (
      ['verification-requested', 'approval-pending', 'completed'].includes(finding.status) &&
      finding.actionSubmissions.length === 0 &&
      finding.historicalCompletion === undefined
    ) {
      context.addIssue({
        code: 'custom',
        path: ['actionSubmissions'],
        message: 'Verification requires at least one action submission',
      });
    }
    if (
      finding.status === 'completed' &&
      finding.verification?.approved !== true &&
      finding.historicalCompletion === undefined
    ) {
      context.addIssue({
        code: 'custom',
        path: ['verification'],
        message: 'A completed finding requires an approved verification',
      });
    }
    if (
      finding.historicalCompletion !== undefined &&
      (finding.status !== 'completed' ||
        finding.verification !== null ||
        finding.actionSubmissions.length !== 0)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['historicalCompletion'],
        message:
          'Historical completion is read-only source provenance and cannot impersonate canonical action or verification',
      });
    }
    if ((finding.status === 'cancelled') !== (finding.cancellation !== null)) {
      context.addIssue({
        code: 'custom',
        path: ['cancellation'],
        message: 'Cancellation evidence is required only for cancelled findings',
      });
    }
    if (Date.parse(finding.updatedAt) < Date.parse(finding.createdAt)) {
      context.addIssue({
        code: 'custom',
        path: ['updatedAt'],
        message: 'updatedAt cannot precede createdAt',
      });
    }
  });

export const findingTransitionActionSchema = z.enum([
  'assign',
  'start-action',
  'request-verification',
  'approve-verification',
  'reject-verification',
  'cancel',
]);

export type Finding = z.infer<typeof findingSchema>;
export type InspectionFindingWorkInput = z.infer<typeof inspectionFindingWorkInputSchema>;
export type FindingStatus = z.infer<typeof findingStatusSchema>;
export type FindingTransitionAction = z.infer<typeof findingTransitionActionSchema>;
export type Inspection = z.infer<typeof inspectionSchema>;

const findingTransitions = {
  received: { assign: 'assigned', cancel: 'cancelled' },
  assigned: { 'start-action': 'action-in-progress', cancel: 'cancelled' },
  'action-in-progress': { 'request-verification': 'verification-requested', cancel: 'cancelled' },
  'verification-requested': {
    'approve-verification': 'completed',
    'reject-verification': 'action-in-progress',
    cancel: 'cancelled',
  },
  'approval-pending': {},
  completed: {},
  cancelled: {},
} as const satisfies Record<FindingStatus, Partial<Record<FindingTransitionAction, FindingStatus>>>;

export function nextFindingStatus(
  status: FindingStatus,
  action: FindingTransitionAction,
): FindingStatus | null {
  return (
    findingTransitions[status][action as keyof (typeof findingTransitions)[typeof status]] ?? null
  );
}

export function canTransitionFinding(
  status: FindingStatus,
  action: FindingTransitionAction,
): boolean {
  return nextFindingStatus(status, action) !== null;
}
